import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { legalContent, ROUTES } from "@/legal";
import { useI18n } from "@/i18n";
import { Footer } from "@/components/site/Footer";

const Body = ({ body }) =>
  body.map((item, i) => {
    if (typeof item === "string")
      return (
        <p key={i} className="mt-4 max-w-2xl font-body text-[15px] leading-relaxed text-[#4a4a45]">
          {item}
        </p>
      );
    if (item.addr)
      return (
        <div key={i} className="mt-4 font-body text-[15px] leading-relaxed text-[#1c1c1a]">
          {item.addr.map((l, k) => (
            <div key={k}>{l}</div>
          ))}
        </div>
      );
    if (item.list)
      return (
        <ul key={i} className="mt-4 max-w-2xl list-none space-y-2 border-l border-[#dcdcd6] pl-5">
          {item.list.map((l, k) => (
            <li key={k} className="font-body text-[15px] leading-relaxed text-[#4a4a45]">
              {l}
            </li>
          ))}
        </ul>
      );
    return null;
  });

export default function LegalPage({ docKey }) {
  const { lang, setLang, t } = useI18n();
  const doc = legalContent[lang][docKey];

  useEffect(() => {
    window.scrollTo(0, 0);
    const link = document.querySelector('link[rel="canonical"]');
    if (link) link.href = `https://bardinepa.ch${ROUTES[docKey]}`;
  }, [docKey, lang]);

  return (
    <div className="min-h-screen bg-[#f4f4f0]">
      {/* Minimal header */}
      <header className="border-b border-[#dcdcd6]">
        <div className="mx-auto flex max-w-[1000px] items-center justify-between px-6 py-6">
          <Link to="/" data-testid="legal-logo" className="font-display text-2xl tracking-tight text-[#1c1c1a]">
            Bardine <span className="text-[#9e8d7b]">PA.</span>
          </Link>
          <div data-testid="lang-switch" className="flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.22em]">
            {["fr", "en"].map((l, i) => (
              <span key={l} className="flex items-center gap-2">
                {i === 1 && <span className="text-[#c4c4bd]">/</span>}
                <button
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`transition-colors ${lang === l ? "text-[#1c1c1a]" : "text-[#a5a59e] hover:text-[#5c5c57]"}`}
                >
                  {l.toUpperCase()}
                </button>
              </span>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1000px] px-6 py-20 md:py-28">
        <Link
          to="/"
          data-testid="legal-back"
          className="link-underline inline-flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.22em] text-[#5c5c57]"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t.footer.backHome}
        </Link>

        <h1 className="mt-10 font-display text-5xl leading-[1.02] tracking-tight text-[#1c1c1a] md:text-6xl">
          {doc.title}
        </h1>
        {doc.intro && (
          <p className="mt-6 max-w-2xl font-body text-base leading-relaxed text-[#4a4a45]">{doc.intro}</p>
        )}

        <div className="mt-14 space-y-14">
          {doc.sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${i}`}>
              <h2 className="font-display text-2xl tracking-tight text-[#1c1c1a]">{s.h}</h2>
              <div className="mt-1 h-px w-10 bg-[#9e8d7b]" />
              <Body body={s.body} />
            </section>
          ))}
        </div>

        <p className="mt-16 font-body text-[11px] uppercase tracking-[0.18em] text-[#9e8d7b]">{doc.updated}</p>

        {/* cross-link to the other legal page */}
        <div className="mt-10 flex flex-wrap gap-6 border-t border-[#dcdcd6] pt-8">
          <Link to={ROUTES.legal} className="link-underline font-body text-[11px] uppercase tracking-[0.22em] text-[#1c1c1a]">
            {t.footer.legalNotice}
          </Link>
          <Link to={ROUTES.privacy} className="link-underline font-body text-[11px] uppercase tracking-[0.22em] text-[#1c1c1a]">
            {t.footer.privacy}
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
